// Plaid Link in the page: loads Plaid's script on first use, and keeps the
// link token in sessionStorage across an OAuth bank's redirect (Amex, Chase,
// Citi and Capital One send the browser to their own site and back to
// /plaid-return). The bank password goes to Plaid's frame, never to this app.

/** Must match the CSP's script-src entry (infra/lib/stack.ts). */
export const PLAID_LINK_SCRIPT = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
export const PLAID_RETURN_PATH = '/plaid-return';

export interface PlaidLinkError {
  error_type: string;
  error_code: string;
  error_message: string;
  display_message: string | null;
}

export interface PlaidSuccessMetadata {
  institution: { name: string; institution_id: string } | null;
}

export interface PlaidExitMetadata {
  institution: { name: string; institution_id: string } | null;
  status: string | null;
}

export interface PlaidHandler {
  open(): void;
  exit(options?: { force?: boolean }): void;
  destroy(): void;
}

export interface PlaidCreateOptions {
  token: string;
  receivedRedirectUri?: string;
  onSuccess(publicToken: string, metadata: PlaidSuccessMetadata): void;
  onExit(error: PlaidLinkError | null, metadata: PlaidExitMetadata): void;
  onEvent?(eventName: string, metadata: unknown): void;
}

export interface PlaidFactory {
  create(options: PlaidCreateOptions): PlaidHandler;
}

declare global {
  interface Window {
    Plaid?: PlaidFactory;
  }
}

let loading: Promise<PlaidFactory> | null = null;

/** Adds Plaid's script once and resolves with window.Plaid. */
export function loadPlaid(): Promise<PlaidFactory> {
  if (window.Plaid) return Promise.resolve(window.Plaid);
  loading ??= new Promise<PlaidFactory>((resolve, reject) => {
    const fail = () => {
      loading = null;
      script.remove();
      reject(new Error("Couldn't load Plaid. Check the connection and try again."));
    };
    const script = document.createElement('script');
    script.src = PLAID_LINK_SCRIPT;
    script.async = true;
    script.onload = () => (window.Plaid ? resolve(window.Plaid) : fail());
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return loading;
}

/** What survives the trip to an OAuth bank and back. No public or access tokens, only the link token. */
export interface SavedLink {
  linkToken: string;
  /** 'new' adds a login; 'update' reconnects `itemId` (Plaid update mode). */
  mode: 'new' | 'update';
  itemId?: string;
  /** Update mode with account selection (Manage accounts). */
  manageAccounts?: boolean;
  institution?: string;
  expires: string;
}

const KEY = 'fourwinds.plaidLink';

export function saveLink(link: SavedLink): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(link));
  } catch {
    // Storage blocked: Link still works; only an OAuth bank's return can't resume.
  }
}

export function readLink(): SavedLink | null {
  try {
    const link = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as SavedLink | null;
    if (!link || typeof link.linkToken !== 'string' || (link.mode !== 'new' && link.mode !== 'update')) return null;
    if (Date.parse(link.expires) < Date.now()) return null;
    return link;
  } catch {
    return null;
  }
}

export function clearLink(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing saved
  }
}

export const onPlaidReturnPath = () => location.pathname === PLAID_RETURN_PATH;

let returnTaken = false;

/** The full return URL (with Plaid's oauth_state_id), once per page load. */
export function takePlaidReturn(): string | null {
  if (returnTaken || !onPlaidReturnPath()) return null;
  returnTaken = true;
  return location.href;
}

/** Leaves /plaid-return for the Connections screen, with no query string left behind. */
export function leavePlaidReturn(): void {
  if (onPlaidReturnPath()) history.replaceState(null, '', '/#/connections');
}

/** A short, plain message for Link's exit error. */
export function friendlyLinkError(err: PlaidLinkError): string {
  switch (err.error_code) {
    case 'INSTITUTION_DOWN':
    case 'INSTITUTION_NOT_RESPONDING':
    case 'INSTITUTION_NOT_AVAILABLE':
      return "The bank isn't responding right now. Try again in a little while.";
    case 'INVALID_LINK_TOKEN':
      return 'That sign-in timed out. Start again.';
    case 'INVALID_CREDENTIALS':
    case 'INVALID_MFA':
    case 'ITEM_LOCKED':
      return "The bank didn't accept that sign-in. Check the details with the account owner and try again.";
    case 'INSTITUTION_NO_LONGER_SUPPORTED':
    case 'NO_ACCOUNTS':
      return err.display_message ?? "Plaid couldn't find accounts it can read at that bank.";
    default:
      return err.display_message ?? "Plaid couldn't finish connecting the bank. Try again.";
  }
}
